import { useEffect, useState } from "react";
import { UserPlus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RemoveUserDialog } from "./RemoveUserDialog";

const ROLE_META = { owner: ["Owner", "bg-[#F9F7F4] text-[#151312]"], admin: ["Admin", "bg-[#E4A853] text-[#151312]"], host: ["Host", "bg-[#302B28] text-white"] };
const cls = "h-10 rounded-lg border border-[#302B28] bg-[#151312] px-3 text-sm text-white outline-none focus:ring-2 focus:ring-[#D96C4A]";

export default function Users() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [users, setUsers] = useState(null);
  const [f, setF] = useState({ email: "", name: "", role: "host" });
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/users", f);
      setUsers((u) => [...u, r.data]);
      setF({ email: "", name: "", role: "host" });
      toast.success(`${r.data.email} added as ${r.data.role}. They sign in with Google using that email.`);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const setRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role });
      setUsers((x) => x.map((y) => (y.user_id === u.user_id ? { ...y, role } : y)));
      toast.success(`${u.name} is now ${role === "admin" ? "an admin" : "a host"}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((x) => x.filter((y) => y.user_id !== u.user_id));
      setRemoving(null);
      toast.success(`${u.name} and all their data were removed`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">Users & roles</h1>
      <p className="mt-2 max-w-2xl text-[#A39E9A]"><b className="text-white">Admins</b> can use this owner dashboard: hosts, payments, orders and pricing. <b className="text-white">Hosts</b> manage their own Airbnbs. Only the owner can add users or change roles.</p>
      {isOwner && (
        <form onSubmit={invite} className="mt-8 flex flex-wrap items-end gap-3 rounded-2xl border border-[#302B28] bg-[#1E1A18] p-5" data-testid="invite-user-form">
          <label className="text-xs text-[#A39E9A]">Google email<input type="email" required className={`${cls} mt-1 block w-64`} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email-input" /></label>
          <label className="text-xs text-[#A39E9A]">Name (optional)<input className={`${cls} mt-1 block w-48`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name-input" /></label>
          <label className="text-xs text-[#A39E9A]">Role<select className={`${cls} mt-1 block w-32`} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="invite-role-select"><option value="host">Host</option><option value="admin">Admin</option></select></label>
          <button disabled={busy} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#D96C4A] px-4 text-sm font-semibold text-white hover:bg-[#C25D3E] disabled:opacity-60" data-testid="invite-submit-button"><UserPlus /> Add user</button>
        </form>
      )}
      <div className="mt-6 overflow-x-auto rounded-2xl border border-[#302B28] bg-[#1E1A18]">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="border-b border-[#302B28] text-left text-xs uppercase tracking-wider text-[#A39E9A]">{["User", "Role", "Status", "Joined", isOwner ? "Change role" : ""].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-[#302B28]">
            {(users || []).map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <td className="px-5 py-3"><p className="font-semibold">{u.name}</p><p className="text-xs text-[#A39E9A]">{u.email}</p></td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_META[u.role]?.[1]}`} data-testid={`user-role-${u.user_id}`}>{ROLE_META[u.role]?.[0] || u.role}</span></td>
                <td className="px-5 py-3 text-[#A39E9A]">{u.status === "invited" ? "Invited, not signed in yet" : u.status === "active" ? "Active" : u.status}</td>
                <td className="px-5 py-3 text-[#A39E9A]">{fmtDate(u.created_at)}</td>
                <td className="px-5 py-3">{isOwner && u.role !== "owner" && (
                  <div className="flex items-center gap-2">
                    <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className={`${cls} h-8`} data-testid={`user-role-select-${u.user_id}`}><option value="host">Host</option><option value="admin">Admin</option></select>
                    <button onClick={() => setRemoving(u)} title="Remove user" data-testid={`user-remove-${u.user_id}`} className="grid h-8 w-8 place-items-center rounded-lg text-[#A39E9A] transition-colors duration-200 hover:bg-[#3A1F18] hover:text-[#F08A68]"><Trash size={16} /></button>
                  </div>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RemoveUserDialog user={removing} onCancel={() => setRemoving(null)} onConfirm={remove} />
    </div>
  );
}
